/**
 * GRAFİK — portföyün günlük seyri (kullanıcı, 12.10.2026).
 *
 * İstek: iki tarih arası (varsayılan son 1 yıl), günlük, seçilen para
 * biriminde (varsayılan USD): toplam varlık, toplam maliyet, toplam getiri,
 * getiri yüzdesi. Getiri için kullanıcı İKİ tanımı da istedi, ayrı çizgiler:
 * "mevcut getiri" (değer − maliyet) ve "geçmiş dahil getiri"
 * (+ satış kazancı + temettü/faiz − masraf; satış yapılınca kırılmaz).
 *
 * SAYILAR MOTORDAN: her nokta o günün rapor toplamları (`valuationSeries`).
 * Burada para toplanmıyor. Çizgiyi çizmek için tutar `Number`a çevriliyor —
 * yalnızca PİKSEL konumu için; ekranda yazan her tutar motorun metni.
 *
 * PARÇA PARÇA, AMA TEK SEFERDE ÇİZİLİR (kullanıcı, 12.10.2026 ikinci tur):
 * her gün bir tam değerleme olduğu için seri aylık parçalarla isteniyor
 * (uygulama donmaz), ama grafik dolarak gelmiyor — hesap bitene kadar ortada
 * dönen gösterge ve "x / y gün", sonra iki grafik birden. Aralık ya da para
 * birimi değişince eski grafik kalkar (başka bir soruya aitti); arka planda
 * fiyat eşitlemesi bitince (`revision`) ESKİ grafik yerinde kalır, yenisi
 * hazır olunca tek seferde yerine geçer — açılışta "iki geçiş" görülmez.
 *
 * YALNIZCA EKSİK GÜNLER (kullanıcı, 12.10.2026): hesaplanan günler bellekte
 * tutulur (`lib/seriOnbellegi.ts`); defter değişmediyse ve eşitleme o güne
 * dokunmadıysa gün yeniden hesaplanmaz. Son birkaç gün (saate bağlı
 * bayatlık, anlık fiyat) her seferinde yeniden hesaplanır.
 *
 * RENK (kullanıcı, 12.10.2026 ikinci tur): toplam varlık koyu yeşil, toplam
 * maliyet lacivert (navy); iki getiri çizgisi YARGI taşır — sıfırın üstü yeşil, altı
 * kırmızı (`--pf-grafik-*`); altları DOLDURULMAZ (kullanıcı, 12.10.2026).
 * Mevcut getiri düz, geçmiş dahil getiri kesikli çizgi. Yokluk: çizgide
 * BOŞLUK (sıfır değil), seçili gün panelinde sebebiyle "—"; "ölçülemedi"
 * işaretli, "yok" işaretsiz.
 */

import { TemettuHaric, temettuHaricMi } from "../components/TemettuHaric";
import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import type { IsoDate } from "@parafon/contracts";
import {
  SERIES_MAX_DAYS,
  type Absence,
  type SeriesField,
  type ValuationSeriesPoint,
} from "@parafon/contracts/local-api";
import { formatMoney, formatRatio } from "../lib/decimal";
import { formatIsoDate } from "../lib/format";
import { useSinirlar } from "../kabiliyet/KabiliyetSaglayici";
import { KilitliKabiliyet } from "../kabiliyet/KilitliKabiliyet";
import { UCRETSIZ_KARSILASTIRMA, enEskiGun } from "../kabiliyet/kabiliyet";
import { bugunYerelIso } from "../lib/tarihDonemleri";
import { onbellekDurumu } from "../lib/seriOnbellegi";
import { gunEkle, seriAnahtari, seriYukle } from "../lib/seriYukle";
import { kapsamBellegi, useKapsamSecenekleri } from "../lib/hesapKapsami";
import { sanalVeriEsitle } from "../lib/sanalHesap";
import { useCentral } from "../central/CentralContext";
import { HesapCokluSecici, KARSILASTIRMA_MAX } from "../components/HesapSecici";
import { Karsilastirma, OlculerPaneli } from "../components/GrafikKarsilastirma";
import { grafikTercihleriniOku, grafikTercihleriniYaz } from "../lib/grafikTercihleri";
import type { DonemKodu } from "../lib/tarihDonemleri";
import {
  RAPOR_PARA_BIRIMLERI,
  grafikParaBiriminiKaydet,
  kayitliGrafikParaBirimi,
} from "../lib/raporParaBirimi";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { EmptyState, ErrorBlock, Field, Notice, Screen, Select, Spinner, cx } from "../ui/primitives";
import { TarihAraligiSecici } from "../ui/TarihAraligiSecici";
import { YakinlastirmaDugmeleri } from "../ui/YakinlastirmaDugmeleri";
import { useYakinlastirma, type Yakinlastirma } from "../lib/yakinlastirma";
import { M, YEREL } from "../dil";
import { motorMetniVeya } from "../dil/motorMetni";
import { eksenTutar } from "../lib/tutarGizleme";

export { PARCA_GUN, gunEkle, parcala } from "../lib/seriYukle";

function gunFarki(from: IsoDate, to: IsoDate): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

// ---------------------------------------------------------------------------
// Çizgiler
// ---------------------------------------------------------------------------

/**
 * Çizginin rengi: sabit bir ton, ya da İŞARETE göre (sıfırın üstü / altı).
 * KURAL (kullanıcı, 12.10.2026): aynı grafikte iki çizgi ASLA aynı renk
 * değil — her getiri çizgisinin üst ve alt rengi kendine ait (dört ayrı
 * ton); sarı kırmızının yanına konmaz (sarı hiç kullanılmıyor).
 */
type Renk =
  | { readonly tur: "sabit"; readonly renk: string }
  | { readonly tur: "isaret"; readonly arti: string; readonly eksi: string };

interface Cizgi {
  /** Seri alanı (`SeriesField`) ya da karşılaştırmada hesap kimliği. */
  readonly alan: string;
  readonly ad: string;
  readonly renk: Renk;
  readonly kesikli?: boolean;
}

const MEVCUT: Renk = { tur: "isaret", arti: "var(--pf-grafik-mevcut-arti)", eksi: "var(--pf-grafik-mevcut-eksi)" };
const GECMIS: Renk = { tur: "isaret", arti: "var(--pf-grafik-gecmis-arti)", eksi: "var(--pf-grafik-gecmis-eksi)" };

export const AD: Readonly<Record<SeriesField, string>> = M.grafik.alan;

const TUTAR_CIZGILERI: readonly Cizgi[] = [
  { alan: "value", ad: AD.value, renk: { tur: "sabit", renk: "var(--pf-grafik-varlik)" } },
  { alan: "cost", ad: AD.cost, renk: { tur: "sabit", renk: "var(--pf-grafik-maliyet)" } },
  { alan: "unrealizedGain", ad: AD.unrealizedGain, renk: MEVCUT },
  { alan: "totalReturn", ad: AD.totalReturn, renk: GECMIS, kesikli: true },
];

const ORAN_CIZGILERI: readonly Cizgi[] = [
  { alan: "unrealizedGainRatio", ad: AD.unrealizedGainRatio, renk: MEVCUT },
  { alan: "totalReturnRatio", ad: AD.totalReturnRatio, renk: GECMIS, kesikli: true },
];

/** Portföy serisinin bir alanı (çizgi grafiği için). */
const seriDegeri = (p: ValuationSeriesPoint, alan: string): string | null => p[alan as SeriesField] ?? null;

/** Seçili gün noktasının rengi. */
function noktaRengi(c: Cizgi, v: number): string {
  if (c.renk.tur === "sabit") return c.renk.renk;
  return v < 0 ? c.renk.eksi : c.renk.arti;
}

// ---------------------------------------------------------------------------
// Ekran
// ---------------------------------------------------------------------------

type Yukleme =
  | { readonly durum: "yukleniyor"; readonly biten: number; readonly toplam: number }
  | { readonly durum: "tamam" }
  | { readonly durum: "hata"; readonly hata: unknown };

/** Hesaplanmış seri(ler) ve HANGİ soruya ait olduğu (aralık + para birimi + hesaplar). */
interface Sonuc {
  readonly anahtar: string;
  /** Kapsam başına bir seri; `id` null = bütün defter. */
  readonly seriler: readonly { readonly id: string | null; readonly points: readonly ValuationSeriesPoint[] }[];
}

export function GrafikScreen() {
  const { repo, revision } = useRepositoryContext();
  const bugun = bugunYerelIso();
  // Gelir modeli: fiyat geçmişi derinliği ve karşılaştırılan hesap sayısı.
  const sinir = useSinirlar();
  const enEski = enEskiGun(sinir.fiyatGecmisiYil, bugun);
  const karsilastirmaEnCok = sinir.ucretli ? KARSILASTIRMA_MAX : UCRETSIZ_KARSILASTIRMA;
  // TERCİHLER hatırlanır (kullanıcı, 12.10.2026): dönem ya da özel aralık ve
  // hangi grafiklerin açık olduğu — `lib/grafikTercihleri.ts`.
  const [tercih, setTercih] = useState(() => grafikTercihleriniOku(bugun));
  const tercihDegistir = (yeni: Partial<typeof tercih>) =>
    setTercih((t) => {
      const sonraki = { ...t, ...yeni };
      grafikTercihleriniYaz(sonraki);
      return sonraki;
    });
  const { from, to } = tercih;
  const setAralik = (a: { from: IsoDate; to: IsoDate }, donem: DonemKodu | null) =>
    tercihDegistir({ from: a.from, to: a.to, donem });
  const gorunumDegistir = (hangi: "tutar" | "yuzde") => {
    const yeni = { tutar: tercih.tutar, yuzde: tercih.yuzde, [hangi]: !tercih[hangi] };
    // Tek açık olan kapatılırsa öbürü açılır (kullanıcı, 12.10.2026): ekran
    // hiçbir zaman boş kalmaz.
    if (!yeni.tutar && !yeni.yuzde) {
      tercihDegistir({ tutar: hangi !== "tutar", yuzde: hangi !== "yuzde" });
      return;
    }
    tercihDegistir(yeni);
  };
  const [currency, setCurrencyState] = useState<string>(kayitliGrafikParaBirimi);
  const setCurrency = useCallback((kod: string) => {
    setCurrencyState(kod);
    grafikParaBiriminiKaydet(kod);
  }, []);

  // HESAP KAPSAMI (back-test, 14.10.2026): hiçbiri seçili değilse bütün
  // defter (gerçek hesaplar); biri seçiliyse o hesap (sanal da olabilir);
  // iki ve fazlası ayrı çizgilerle karşılaştırılır. Seçim yalnızca bellekte.
  const merkez = useCentral();
  const kapsamVerisi = useKapsamSecenekleri(repo, revision);
  const [secilenHesaplar, setSecilenHesaplarState] = useState<readonly string[]>(kapsamBellegi.grafik);
  const setSecilenHesaplar = useCallback((ids: readonly string[]) => {
    setSecilenHesaplarState(ids);
    kapsamBellegi.grafikYaz(ids);
  }, []);
  const hesaplar = useMemo(
    () =>
      kapsamVerisi.yukleniyor
        ? secilenHesaplar
        : secilenHesaplar
            .filter((id) => kapsamVerisi.secenekler.some((x) => x.id === id))
            .slice(0, karsilastirmaEnCok),
    [secilenHesaplar, kapsamVerisi, karsilastirmaEnCok],
  );
  const karsilastirma = hesaplar.length >= 2;
  // Tek sanal hesap seçili ve kuralı temettüyü yok sayıyor (19.10.2026).
  const temettuHaric = hesaplar.length === 1 && temettuHaricMi(kapsamVerisi.sanallar.find((x) => x.id === hesaplar[0]));
  // Hesap listesi gelmeden seçili hesabın sanal mı olduğu bilinmez; bütün
  // defter bunu beklemez (liste gelince yeniden hesap açılmasın).
  const kapsamBekleniyor = hesaplar.length > 0 && kapsamVerisi.yukleniyor;
  const kapsamlar: readonly (string | null)[] = hesaplar.length === 0 ? [null] : hesaplar;
  const sanallarRef = useRef(kapsamVerisi.sanallar);
  sanallarRef.current = kapsamVerisi.sanallar;
  const merkezRef = useRef(merkez);
  merkezRef.current = merkez;

  const anahtar = `${from}|${to}|${currency}|${hesaplar.join(",")}`;
  const [sonuc, setSonuc] = useState<Sonuc | null>(null);
  const [yukleme, setYukleme] = useState<Yukleme>({ durum: "yukleniyor", biten: 0, toplam: 0 });
  const [yeniden, setYeniden] = useState(0);

  const tarihHatasi =
    from === "" || to === ""
      ? M.grafik.tarih.ikiTarih
      : from > to
        ? M.grafik.tarih.sira
        : gunFarki(from, to) + 1 > SERIES_MAX_DAYS
          ? M.grafik.tarih.enCokGun(SERIES_MAX_DAYS)
          : null;
  // Fiyat geçmişi derinliğinden eski başlangıç: hesaplanmaz, kilit ifadesi.
  const gecmisKilidi = tarihHatasi === null && enEski !== undefined && from < enEski;
  const aralikHatasi = tarihHatasi ?? (gecmisKilidi ? "fiyat-gecmisi" : null);

  useEffect(() => {
    if (aralikHatasi !== null) return;
    if (kapsamBekleniyor) return;
    let iptal = false;
    // ÖNBELLEK: yalnızca daha önce hesaplanmamış (ya da fiyatı/defteri o
    // günden sonra değişmiş) günler istenir — `lib/seriOnbellegi.ts`.
    const bugunSimdi = bugunYerelIso();
    const birlestir = (
      elde: ReadonlyMap<IsoDate, ValuationSeriesPoint>,
      yeni: ReadonlyMap<IsoDate, ValuationSeriesPoint>,
    ): ValuationSeriesPoint[] => {
      const out: ValuationSeriesPoint[] = [];
      for (let d = from; d <= to; d = gunEkle(d, 1)) {
        const p = yeni.get(d) ?? elde.get(d);
        if (p !== undefined) out.push(p);
      }
      return out;
    };
    const anahtarSimdi = anahtar;
    const hesapIdleri = (k: string | null) => (k === null ? undefined : [k]);
    const ilkler = kapsamlar.map((k) => onbellekDurumu(repo, seriAnahtari(currency, hesapIdleri(k)), from, to, bugunSimdi));
    if (ilkler.every((i) => i.eksikler.length === 0)) {
      setSonuc({
        anahtar: anahtarSimdi,
        seriler: kapsamlar.map((k, i) => ({ id: k, points: birlestir((ilkler[i] as (typeof ilkler)[number]).elde, new Map()) })),
      });
      setYukleme({ durum: "tamam" });
      return;
    }
    const toplamEksik = ilkler.reduce((a, i) => a + i.eksikGun, 0);
    setYukleme({ durum: "yukleniyor", biten: 0, toplam: toplamEksik });
    // Parçalar `seriYukle`de birikir; ekrana yalnızca seri tamamlanınca geçer.
    void (async () => {
      const seriler: { id: string | null; points: ValuationSeriesPoint[] }[] = [];
      let onceki = 0;
      for (const [i, k] of kapsamlar.entries()) {
        // Sanal hesabın varlıklarının fiyatı önce eşitlenir (bir oturumda bir kez).
        const sanal = k === null ? undefined : sanallarRef.current.find((x) => x.id === k);
        if (sanal !== undefined) await sanalVeriEsitle(repo, merkezRef.current, sanal, currency);
        if (iptal) return;
        const tabani = onceki;
        const points = await seriYukle(repo, currency, from, to, {
          bugun: bugunSimdi,
          iptal: () => iptal,
          ...(k === null ? {} : { accountIds: [k] }),
          ilerleme: (biten, toplam) => {
            if (iptal) return;
            // Tek kapsamda sayıyı `seriYukle` söyler (kalıcı kopyadan gelenler düşülmüş).
            if (kapsamlar.length === 1) setYukleme({ durum: "yukleniyor", biten, toplam });
            else setYukleme({ durum: "yukleniyor", biten: tabani + biten, toplam: toplamEksik });
          },
        });
        if (iptal || points === null) return;
        onceki += (ilkler[i] as (typeof ilkler)[number]).eksikGun;
        seriler.push({ id: k, points });
      }
      if (iptal) return;
      setSonuc({ anahtar: anahtarSimdi, seriler });
      setYukleme({ durum: "tamam" });
    })().catch((hata: unknown) => {
      if (!iptal) setYukleme({ durum: "hata", hata });
    });
    return () => {
      iptal = true;
    };
    // `kapsamlar` ve `anahtar` hesap listesinden türer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo, revision, from, to, currency, aralikHatasi, yeniden, anahtar, kapsamBekleniyor]);

  // Gösterilen seri: yalnızca ŞU ANKİ soruya aitse (eşitleme sonrası
  // yenilenirken eskisi yerinde kalır; aralık değişince kalkar).
  const seriler = useMemo(
    () => (sonuc !== null && sonuc.anahtar === anahtar ? sonuc.seriler : []),
    [sonuc, anahtar],
  );
  const points = useMemo(() => (karsilastirma ? [] : (seriler[0]?.points ?? [])), [seriler, karsilastirma]);

  // Seçili gün: üzerine gelinen ya da klavyeyle seçilen; yoksa son nokta.
  const [secili, setSecili] = useState<number | null>(null);
  useEffect(() => setSecili(null), [from, to, currency, anahtar]);
  const seciliIndeks = secili !== null && secili < points.length ? secili : points.length - 1;
  const seciliNokta = seciliIndeks >= 0 ? points[seciliIndeks] : undefined;

  const [kapali, setKapali] = useState<ReadonlySet<string>>(new Set());
  // İki grafik (Tutarlar, Getiri yüzdesi) aynı günleri gösterir: birlikte yakınlaşır.
  const yakin = useYakinlastirma(points.length);
  const degistir = (alan: string) =>
    setKapali((k) => {
      const yeni = new Set(k);
      if (yeni.has(alan)) yeni.delete(alan);
      else yeni.add(alan);
      return yeni;
    });

  const hicDegerYok =
    points.length > 0 &&
    points.every((p) => p.value === null && p.absent.value?.kind === "yok" && p.totalReturn === null);

  const hesaplaniyor = aralikHatasi === null && yukleme.durum === "yukleniyor";

  return (
    <Screen
      title={M.grafik.baslik}
      description={M.grafik.aciklama}
      toolbar={
        // Telefonda iki sütunlu ızgara (dönem ve para birimi tam satır, iki
        // tarih yan yana); genişte tek satır.
        <div className="grid w-full grid-cols-2 items-end gap-2 sm:flex sm:w-auto sm:flex-wrap">
          <div className="col-span-2 min-w-0 sm:col-span-1">
            <HesapCokluSecici
              secenekler={kapsamVerisi.secenekler}
              secili={hesaplar}
              onChange={setSecilenHesaplar}
              enCok={karsilastirmaEnCok}
            />
          </div>
          <TarihAraligiSecici
            idOnek="grafik"
            from={from}
            to={to}
            max={bugun}
            {...(enEski === undefined ? {} : { min: enEski })}
            bugun={bugun}
            onChange={setAralik}
            donem={tercih.donem}
            alanSinifi="min-w-0 sm:w-40"
            donemSinifi="col-span-2 sm:col-span-1"
          />
          <Field label={M.grafik.paraBirimi} htmlFor="grafik-para" className="col-span-2 min-w-0 sm:col-span-1 sm:w-52">
            <Select
              id="grafik-para"
              value={currency}
              options={[...RAPOR_PARA_BIRIMLERI]}
              onChange={(e) => setCurrency(e.currentTarget.value)}
            />
          </Field>
          <div className="col-span-2 flex min-w-0 flex-col gap-1 sm:col-span-1">
            <span id="grafik-gorunum" className="text-[12px] font-semibold text-muted">
              {M.grafik.gorunum}
            </span>
            <div role="group" aria-labelledby="grafik-gorunum" className="flex gap-1.5">
              {(
                [
                  ["tutar", M.grafik.tutarlar],
                  ["yuzde", M.grafik.getiriYuzdesi],
                ] as const
              ).map(([k, ad]) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={tercih[k]}
                  onClick={() => gorunumDegistir(k)}
                  className={cx(
                    "h-[var(--pf-control-h)] flex-1 rounded-[var(--pf-radius-sm)] border px-3 text-[13px] whitespace-nowrap sm:flex-none",
                    tercih[k]
                      ? "border-accent bg-accent-soft text-ink"
                      : "border-line-strong bg-surface text-faint",
                  )}
                >
                  {ad}
                </button>
              ))}
            </div>
          </div>
        </div>
      }
    >
      <div className="space-y-4 p-4">
        {tarihHatasi !== null && (
          <Notice tone="warn" title={M.grafik.tarih.baslik}>
            {tarihHatasi}
          </Notice>
        )}
        {gecmisKilidi && (
          <KilitliKabiliyet
            ne={M.grafik.gecmisKilidi(sinir.fiyatGecmisiYil ?? 0)}
            aciklama={M.grafik.gecmisKilidiAciklama}
          />
        )}
        {yukleme.durum === "hata" && (
          <ErrorBlock error={yukleme.hata} onRetry={() => setYeniden((x) => x + 1)} />
        )}
        {hesaplaniyor && points.length === 0 && (
          <HesapGostergesi biten={yukleme.biten} toplam={yukleme.toplam} />
        )}
        {hesaplaniyor && points.length > 0 && (
          <p className="flex justify-end">
            <Spinner label={M.grafik.guncelleniyor} />
          </p>
        )}
        {/* Ölçüler grafiklerin ÖNÜNDE (kullanıcı kararı 19.10.2026): Dönemsel
            Getiri'deki Özet ile aynı düzen — önce sayılar, sonra çizgi. */}
        {aralikHatasi === null && seriler.length > 0 && !hicDegerYok && (
          <OlculerPaneli
            seriler={seriler}
            secenekler={kapsamVerisi.secenekler}
            currency={currency}
            from={from}
            to={to}
          />
        )}
        {karsilastirma && aralikHatasi === null && seriler.length > 0 && (
          <Karsilastirma seriler={seriler} secenekler={kapsamVerisi.secenekler} currency={currency} />
        )}
        {!karsilastirma && hicDegerYok ? (
          <EmptyState
            title={M.grafik.bosBaslik}
            description={M.grafik.bosAciklama}
          />
        ) : (
          !karsilastirma &&
          aralikHatasi === null &&
          points.length > 0 && (
            <>
              {tercih.tutar && (
              <CizgiGrafik
                baslik={M.grafik.tutarlarBaslik(currency)}
                {...(temettuHaric ? { alt: <TemettuHaric /> } : {})}
                cizgiler={TUTAR_CIZGILERI}
                kapali={kapali}
                onDegistir={degistir}
                points={points}
                secili={seciliIndeks}
                onSec={setSecili}
                eksen={(n) => eksenTutar(n)}
                sifir
                deger={seriDegeri}
                balon={(p, x, sagda) => <GunBalonu p={p} currency={currency} x={x} sagda={sagda} />}
                yakin={yakin}
              />
              )}
              {tercih.yuzde && (
              <CizgiGrafik
                baslik={M.grafik.getiriYuzdesi}
                {...(temettuHaric ? { alt: <TemettuHaric /> } : {})}
                cizgiler={ORAN_CIZGILERI}
                kapali={kapali}
                onDegistir={degistir}
                points={points}
                secili={seciliIndeks}
                onSec={setSecili}
                eksen={(n) => M.bicim.yuzde((n * 100).toLocaleString(YEREL, { maximumFractionDigits: 1 }))}
                sifir
                deger={seriDegeri}
                balon={(p, x, sagda) => <GunBalonu p={p} currency={currency} x={x} sagda={sagda} />}
                yakin={yakin}
              />
              )}
              {/* Fareli cihazda bu bilgiler grafiğin balonunda (kullanıcı,
                  14.10.2026); dokunmatikte üzerine gelme olmadığı için panel kalır. */}
              {seciliNokta !== undefined && (
                <div className="pointer-fine:hidden">
                  <SeciliGun p={seciliNokta} currency={currency} />
                </div>
              )}
            </>
          )
        )}
      </div>
    </Screen>
  );
}

/**
 * Hesap sürerken grafiğin yerinde duran gösterge: ortada dönen halka ve kaç
 * günün hesaplandığı. Grafik dolarak gelmez (kullanıcı, 12.10.2026).
 */
function HesapGostergesi({ biten, toplam }: { readonly biten: number; readonly toplam: number }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="pf-card flex flex-col items-center justify-center gap-3 py-16 text-center"
    >
      <span
        aria-hidden="true"
        className="inline-block size-8 animate-spin rounded-full border-[3px] border-line-strong border-t-accent"
      />
      <span className="text-[13px] text-muted">
        {M.grafik.hesaplaniyor(biten, toplam)}
      </span>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Seçili gün paneli — motorun metinleri
// ---------------------------------------------------------------------------

const SECILI_SATIRLAR: readonly { readonly alan: SeriesField; readonly ad: string; readonly oran?: boolean }[] = [
  { alan: "value", ad: AD.value },
  { alan: "cost", ad: AD.cost },
  { alan: "unrealizedGain", ad: AD.unrealizedGain },
  { alan: "unrealizedGainRatio", ad: AD.unrealizedGainRatio, oran: true },
  { alan: "totalReturn", ad: AD.totalReturn },
  { alan: "totalReturnRatio", ad: AD.totalReturnRatio, oran: true },
];

function SeciliGun({ p, currency }: { readonly p: ValuationSeriesPoint; readonly currency: string }) {
  const satirlar = SECILI_SATIRLAR;
  return (
    <section aria-label={M.grafik.seciliGun} className="pf-card p-3" data-kart-bolum="ozet">
      <p className="text-[13px] font-semibold text-ink">
        {formatIsoDate(p.date)}
        {p.stale && (
          <span className="ml-2 text-[12px] font-normal text-warn">
            {M.grafik.bayatGun}
          </span>
        )}
      </p>
      <dl className="mt-2 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        {satirlar.map((s) => {
          const v = p[s.alan];
          return (
            <div key={s.alan} className="flex items-baseline justify-between gap-3 border-b border-line pb-1">
              <dt className="text-[12px] text-muted">{s.ad}</dt>
              <dd className="text-right">
                {v === null ? (
                  <YoklukMetni yokluk={p.absent[s.alan]} />
                ) : (
                  <span className="pf-num text-[13px] text-ink">
                    {s.oran === true ? formatRatio(v) : formatMoney(v, { signed: s.alan !== "value" && s.alan !== "cost" })}
                    {s.oran !== true && <span className="ml-1 text-[11px] text-faint">{currency}</span>}
                  </span>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

/**
 * Grafiğin üzerine gelinen günün balonu — "Seçili gün" panelinin içeriği
 * (kullanıcı, 14.10.2026). Fareyi engellemez; noktanın boş tarafında.
 */
function GunBalonu({
  p,
  currency,
  x,
  sagda,
}: {
  readonly p: ValuationSeriesPoint;
  readonly currency: string;
  readonly x: number;
  readonly sagda: boolean;
}) {
  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute top-2 z-10 w-max max-w-[20rem] rounded-[var(--pf-radius-sm)] border border-line bg-surface p-2 text-[11px] shadow-md"
      style={sagda ? { left: x + 12 } : { right: `calc(100% - ${x - 12}px)` }}
    >
      <p className="font-semibold text-ink">
        {formatIsoDate(p.date)}
        {p.stale && <span className="ml-2 font-normal text-warn">{M.grafik.bayatOlcum}</span>}
      </p>
      <table className="mt-1 text-[11px] leading-snug">
        <tbody>
          {SECILI_SATIRLAR.map((s) => {
            const v = p[s.alan];
            return (
              <tr key={s.alan}>
                <th scope="row" className="pr-3 text-left font-normal whitespace-nowrap text-muted">
                  {s.ad}
                </th>
                <td className="pf-num text-right whitespace-nowrap text-ink">
                  {v === null ? (
                    <span className={p.absent[s.alan]?.kind === "yok" ? "text-faint" : "text-warn"}>—</span>
                  ) : s.oran === true ? (
                    formatRatio(v)
                  ) : (
                    <>
                      {formatMoney(v, { signed: s.alan !== "value" && s.alan !== "cost" })}
                      <span className="ml-1 text-faint">{currency}</span>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function YoklukMetni({ yokluk }: { readonly yokluk: Absence | undefined }) {
  const olculemedi = yokluk === undefined || yokluk.kind === "olculemedi";
  return (
    <span className={cx("block text-[12px]", olculemedi ? "text-warn" : "text-faint")}>
      —{" "}
      <span className="text-[11px]">
        {motorMetniVeya(yokluk?.reason, M.grafik.motorSoylemedi)}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Çizgi grafiği (SVG; dış kütüphane yok)
// ---------------------------------------------------------------------------

/** Çizim alanının yüksekliği (kullanıcı, 12.10.2026: en az iki katı — 220'den). */
export const YUKSEKLIK = 460;
const SOL = 56;
const SAG = 12;
const UST = 10;
const ALT = 22;

/** Kılavuz adımı: 1, 2, 2.5, 5 × 10ⁿ. */
export function guzelAdim(aralik: number): number {
  if (!(aralik > 0)) return 1;
  const ham = aralik / 4;
  const us = 10 ** Math.floor(Math.log10(ham));
  for (const k of [1, 2, 2.5, 5, 10]) if (ham <= k * us) return k * us;
  return 10 * us;
}

/** Kılavuz çizgilerinin değerleri; kayan nokta artığı sıfırı "-0" yazdırmasın. */
export function kilavuzDegerleri(min: number, max: number, adim: number): number[] {
  const out: number[] = [];
  for (let v = min; v <= max + adim / 2; v += adim) out.push(Math.abs(v) < adim / 1e6 ? 0 : v);
  return out;
}

/** Çizgi grafiği: noktanın türü serbest; değeri `deger`, balonu `balon` verir. */
export function CizgiGrafik<P extends { readonly date: IsoDate }>({
  baslik,
  cizgiler,
  kapali,
  onDegistir,
  points,
  secili,
  onSec,
  eksen,
  sifir = false,
  deger,
  balon,
  yakin: disYakin,
  alt,
}: {
  readonly baslik: string;
  /** Başlığın altında küçük etiket (ör. "temettü hariç"). */
  readonly alt?: ReactNode;
  readonly cizgiler: readonly Cizgi[];
  readonly kapali: ReadonlySet<string>;
  readonly onDegistir: (alan: string) => void;
  readonly points: readonly P[];
  readonly secili: number;
  readonly onSec: (i: number | null) => void;
  readonly eksen: (n: number) => string;
  readonly sifir?: boolean;
  readonly deger: (p: P, alan: string) => string | null;
  readonly balon: (p: P, x: number, sagda: boolean) => ReactNode;
  /** Ortak yakınlaştırma (aynı günleri gösteren grafikler birlikte); yoksa kendi. */
  readonly yakin?: Yakinlastirma;
}) {
  const kendiYakin = useYakinlastirma(points.length);
  const yakin = disYakin ?? kendiYakin;
  // Sürükleyerek aralık seçimi (yalnızca fare).
  const [surukle, setSurukle] = useState<{ readonly a: number; readonly b: number } | null>(null);
  const kapRef = useRef<HTMLDivElement | null>(null);
  // Balon: fare üstündeyken ya da klavyeyle odaktayken.
  const [uzerinde, setUzerinde] = useState(false);
  const kirpOnek = useId().replace(/:/g, "");
  const [genislik, setGenislik] = useState(720);
  useEffect(() => {
    const el = kapRef.current;
    if (el === null) return;
    const olc = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setGenislik(Math.round(w));
    };
    olc();
    const ro = new ResizeObserver(olc);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const acik = cizgiler.filter((c) => !kapali.has(c.alan));
  // YAKINLAŞTIRMA (kullanıcı, 15.10.2026): yalnızca `bas`..`son` çizilir;
  // dikey eksen görünen günlere göre.
  const { bas, son } = yakin.pencere;
  const gorunen = useMemo(() => points.slice(bas, son + 1), [points, bas, son]);
  const n = Math.max(son - bas + 1, 2);
  const icGenislik = Math.max(genislik - SOL - SAG, 10);
  const icYukseklik = YUKSEKLIK - UST - ALT;

  const { min, max, adim } = useMemo(() => {
    let lo = Number.POSITIVE_INFINITY;
    let hi = Number.NEGATIVE_INFINITY;
    for (const p of gorunen) {
      for (const c of acik) {
        const v = deger(p, c.alan);
        if (v === null) continue;
        const x = Number(v);
        if (x < lo) lo = x;
        if (x > hi) hi = x;
      }
    }
    if (sifir) {
      lo = Math.min(lo, 0);
      hi = Math.max(hi, 0);
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return { min: 0, max: 1, adim: 0.25 };
    if (lo === hi) {
      lo -= 1;
      hi += 1;
    }
    const a = guzelAdim(hi - lo);
    return { min: Math.floor(lo / a) * a, max: Math.ceil(hi / a) * a, adim: a };
    // `deger` her çizimde yeni olabilir; noktalar ve açık çizgiler belirleyici.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gorunen, acik, sifir]);

  const x = (i: number) => SOL + (icGenislik * (i - bas)) / (n - 1);
  const gorunur = (i: number) => i >= bas && i <= son;
  const y = (v: number) => UST + icYukseklik - ((v - min) / (max - min)) * icYukseklik;

  // Yokluk çizgiyi KESER (sıfır çizilmez).
  const y0 = y(0);
  const yollar = acik.map((c) => {
    let d = "";
    let kalem = false;
    gorunen.forEach((p, k) => {
      const v = deger(p, c.alan);
      if (v === null) {
        kalem = false;
        return;
      }
      d += `${kalem ? "L" : "M"}${x(bas + k).toFixed(1)},${y(Number(v)).toFixed(1)}`;
      kalem = true;
    });
    return { c, d };
  });
  const ustKirp = `${kirpOnek}-ust`;
  const altKirp = `${kirpOnek}-alt`;

  const kilavuz = kilavuzDegerleri(min, max, adim);

  // Ay başları x ekseninde (en çok ~6 etiket).
  const aylar: { i: number; etiket: string }[] = [];
  points.forEach((p, i) => {
    if (gorunur(i) && p.date.endsWith("-01")) {
      aylar.push({ i, etiket: `${p.date.slice(5, 7)}.${p.date.slice(2, 4)}` });
    }
  });
  const aySeyrek = Math.max(1, Math.ceil(aylar.length / Math.max(2, Math.floor(icGenislik / 70))));

  const indeksOf = (clientX: number, svg: SVGSVGElement) => {
    const r = svg.getBoundingClientRect();
    const i = bas + Math.round(((clientX - r.left - SOL) / icGenislik) * (n - 1));
    return Math.min(Math.max(i, bas), Math.min(son, points.length - 1));
  };
  const merkez = gorunur(secili) ? secili : Math.round((bas + son) / 2);

  const onKlavye = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key === "ArrowLeft") onSec(Math.max(bas, secili - 1));
    else if (e.key === "ArrowRight") onSec(Math.min(son, secili + 1));
    else if (e.key === "Home") onSec(bas);
    else if (e.key === "End") onSec(son);
    else if (e.key === "+" || e.key === "=") yakin.yakinlas(merkez);
    else if (e.key === "-") yakin.uzaklas(merkez);
    else if (e.key === "0") yakin.sifirla();
    else return;
    e.preventDefault();
  };

  const sec = gorunur(secili) ? points[secili] : undefined;

  return (
    <section aria-label={baslik} className="pf-card p-3" data-kart-bolum="grafik">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <h2 className="text-[13px] font-semibold text-ink">{baslik}</h2>
            {alt}
          </div>
          <YakinlastirmaDugmeleri y={yakin} merkez={merkez} baslik={baslik} />
        </div>
        <div role="group" aria-label={M.grafik.cizgi.cizgiler(baslik)} className="flex flex-wrap gap-1.5">
          {cizgiler.map((c) => {
            const gorunur = !kapali.has(c.alan);
            return (
              <button
                key={c.alan}
                type="button"
                aria-pressed={gorunur}
                onClick={() => onDegistir(c.alan)}
                className={cx(
                  "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px]",
                  gorunur ? "border-line text-ink" : "border-dashed border-line text-faint",
                )}
              >
                <svg width="18" height="6" aria-hidden="true" opacity={gorunur ? 1 : 0.35}>
                  {c.renk.tur === "sabit" ? (
                    <line x1="0" y1="3" x2="18" y2="3" stroke={c.renk.renk} strokeWidth="2.5"
                      strokeDasharray={c.kesikli === true ? "4 3" : undefined} />
                  ) : (
                    <>
                      <line x1="0" y1="3" x2="9" y2="3" stroke={c.renk.arti} strokeWidth="2.5"
                        strokeDasharray={c.kesikli === true ? "3 2" : undefined} />
                      <line x1="9" y1="3" x2="18" y2="3" stroke={c.renk.eksi} strokeWidth="2.5"
                        strokeDasharray={c.kesikli === true ? "3 2" : undefined} />
                    </>
                  )}
                </svg>
                {c.ad}
              </button>
            );
          })}
        </div>
      </div>
      <div ref={kapRef} className="relative mt-2 w-full">
        <svg
          width={genislik}
          height={YUKSEKLIK}
          viewBox={`0 0 ${genislik} ${YUKSEKLIK}`}
          style={{ maxWidth: "100%", height: "auto" }}
          role="img"
          aria-label={`${M.grafik.cizgi.svgGun(baslik, points.length)}${yakin.tam ? "" : M.grafik.cizgi.svgGorunen(son - bas + 1)}${M.grafik.cizgi.svgYardim}`}
          tabIndex={0}
          className="block touch-pan-y outline-none select-none focus-visible:ring-2 focus-visible:ring-[var(--pf-focus)]"
          onPointerMove={(e) => {
            if (e.pointerType !== "touch") setUzerinde(true);
            const i = indeksOf(e.clientX, e.currentTarget);
            onSec(i);
            if (surukle !== null) setSurukle({ a: surukle.a, b: i });
          }}
          onPointerDown={(e) => {
            const i = indeksOf(e.clientX, e.currentTarget);
            onSec(i);
            if (e.pointerType === "mouse" && e.button === 0) setSurukle({ a: i, b: i });
          }}
          onPointerUp={() => {
            if (surukle !== null) yakin.araligaGit(surukle.a, surukle.b);
            setSurukle(null);
          }}
          onDoubleClick={yakin.sifirla}
          onPointerLeave={() => {
            setUzerinde(false);
            setSurukle(null);
          }}
          onFocus={() => setUzerinde(true)}
          onBlur={() => setUzerinde(false)}
          onKeyDown={onKlavye}
        >
          {kilavuz.map((v) => (
            <g key={v}>
              <line
                x1={SOL}
                x2={SOL + icGenislik}
                y1={y(v)}
                y2={y(v)}
                stroke="var(--pf-border)"
                strokeWidth={sifir && Math.abs(v) < adim / 1e6 ? 1.5 : 1}
              />
              <text x={SOL - 6} y={y(v) + 3.5} textAnchor="end" fontSize="10" fill="var(--pf-text-muted)">
                {eksen(v)}
              </text>
            </g>
          ))}
          {aylar
            .filter((_, k) => k % aySeyrek === 0)
            .map((a) => (
              <text
                key={a.i}
                x={x(a.i)}
                y={YUKSEKLIK - 6}
                textAnchor="middle"
                fontSize="10"
                fill="var(--pf-text-muted)"
              >
                {a.etiket}
              </text>
            ))}
          <defs>
            <clipPath id={ustKirp}>
              <rect x={0} y={0} width={genislik} height={Math.max(y0, 0)} />
            </clipPath>
            <clipPath id={altKirp}>
              <rect x={0} y={y0} width={genislik} height={Math.max(YUKSEKLIK - y0, 0)} />
            </clipPath>
          </defs>
          {surukle !== null && surukle.a !== surukle.b && (
            <rect
              data-secim="yakinlastirma"
              x={Math.min(x(surukle.a), x(surukle.b))}
              y={UST}
              width={Math.abs(x(surukle.b) - x(surukle.a))}
              height={icYukseklik}
              fill="var(--pf-accent)"
              opacity={0.12}
            />
          )}
          {/* İşaretli çizgi: üst kırpımda yeşil, alt kırpımda kırmızı — sıfırı
              kestiği yerde renk değişir. Altı DOLDURULMAZ (kullanıcı, 12.10.2026). */}
          {yollar.map(({ c, d }) => {
            const ortak = {
              d,
              fill: "none",
              strokeWidth: 1.8,
              strokeLinejoin: "round" as const,
              strokeDasharray: c.kesikli === true ? "5 4" : undefined,
            };
            return c.renk.tur === "sabit" ? (
              <path key={c.alan} data-cizgi={c.alan} stroke={c.renk.renk} {...ortak} />
            ) : (
              <g key={c.alan} data-cizgi={c.alan}>
                <path data-isaret="arti" stroke={c.renk.arti} clipPath={`url(#${ustKirp})`} {...ortak} />
                <path data-isaret="eksi" stroke={c.renk.eksi} clipPath={`url(#${altKirp})`} {...ortak} />
              </g>
            );
          })}
          {sec !== undefined && (
            <g>
              <line
                x1={x(secili)}
                x2={x(secili)}
                y1={UST}
                y2={UST + icYukseklik}
                stroke="var(--pf-text-muted)"
                strokeWidth="1"
                strokeDasharray="2 3"
              />
              {acik.map((c) => {
                const v = deger(sec, c.alan);
                return v === null ? null : (
                  <circle key={c.alan} cx={x(secili)} cy={y(Number(v))} r="3.5" fill={noktaRengi(c, Number(v))} />
                );
              })}
            </g>
          )}
        </svg>
        {uzerinde && surukle === null && sec !== undefined && balon(sec, x(secili), x(secili) < genislik / 2)}
      </div>
    </section>
  );
}
